import React, { FC, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFormContext } from "react-hook-form";

import { Button, Typography } from "ui-library/atoms";
import { ContentBox } from "ui-library/layouts";
import { MultiDataTableWrapper, SimpleTable } from "ui-library/modules";

import i18n from "core/i18n";
import modal from "core/modal";
import { Field } from "core/hookForm";
import { Attachment, FileItem, FileItemStatusEnum } from "types";

import { MODAL_UPLOAD_ATTACHMENT, SECTION_MARGIN } from "../constants";
import { VehicleSubjectChangeFormValues } from "../types/VehicleSubjectChangeFormValues";
import useUploadRemoveAttachment from "../hooks/useUploadRemoveAttachment";

import { AttachmentUploadModal } from "../modal";
import { FileItemsStatus } from "./FileItemsStatus";

const FILE_ROW_HEIGHT = 42;
const EXPANDABLE_ROW_PADDING = 80;

type AttachmentsSectionProps = {
    attachments: Attachment[];
};

export const AttachmentsSection: FC<AttachmentsSectionProps> = ({ attachments }) => {
    const dispatch = useDispatch();
    const { t } = i18n.useTranslation();
    const { idObject } = useParams();

    const { getValues, watch } = useFormContext<VehicleSubjectChangeFormValues>();
    const changeSheetId = getValues("idChange");
    const { removeAttachment } = useUploadRemoveAttachment(idObject, changeSheetId);

    const uploadedAttachments = watch("attachments") || [];

    const mandatoryAttachments = attachments.filter((attachment: Attachment) => !attachment.isOptional);
    const optionalAttachments = attachments.filter((attachment: Attachment) => attachment.isOptional);

    const [attachmentTypeToUpload, setAttachmentTypeToUpload] = useState<string>(null);

    const [openedAttachmentsRows, setOpenedAttachmentsRows] = useState([]);

    const openAttachmentRow = (type: string) => {
        if (!openedAttachmentsRows.includes(type)) {
            setOpenedAttachmentsRows([...openedAttachmentsRows, type]);
        }
    };

    const toggleAttachmentRow = (openedRows) => {
        setOpenedAttachmentsRows(openedRows);
    };

    const openUploadDocumentDialog = (type: string) => {
        setAttachmentTypeToUpload(type);
        openAttachmentRow(type);
        dispatch(modal.openModalForm(MODAL_UPLOAD_ATTACHMENT));
    };

    const onRemoveAttachment = async (file: FileItem) => {
        if (file.uploadStatus === FileItemStatusEnum.UPLOADED && !!file.uploadId) {
            await removeAttachment(file.uploadId);
        }
    };

    const columns = (label) => [
        {
            field: "label",
            headerName: label,
            flex: 3.5,
            sortable: false,
        },
        {
            field: "type",
            headerName: t("common.insertDocument"),
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Button
                    onClick={() => openUploadDocumentDialog(params?.row?.type)}
                    rounded
                    variant="text"
                    color="blue"
                    startIcon={"file-plus"}
                />
            ),
        },
        {
            field: "attachmentStatus",
            headerName: t("vehicle.change.subject.documents.attachmentStatus"),
            flex: 0.5,
            sortable: false,
            renderCell: (params) => <FileItemsStatus fileItems={uploadedAttachments[params?.row?.type]} />,
        },
        {
            field: "expandableUploadedList",
            headerName: t("vehicle.change.subject.documents.attachmentStatus"),
            flex: 0.5,
            sortable: false,
            hide: true,
            renderCell: (data) => (
                <Field<VehicleSubjectChangeFormValues>
                    name={`attachments.${data?.type}`}
                    input="fileList"
                    onDeleteFile={onRemoveAttachment}
                />
            ),
        },
    ];

    const getDetailPanelHeight = (row) => {
        const attachmentsByType = getValues(`attachments.${row.type}`) || [];
        return EXPANDABLE_ROW_PADDING + attachmentsByType.length * FILE_ROW_HEIGHT;
    };

    return (
        <>
            <ContentBox fullWidth textAlign={"center"} mt={SECTION_MARGIN}>
                <Typography variant="h3">{t("change.attachments")}</Typography>
            </ContentBox>

            <MultiDataTableWrapper withWrapper>
                {mandatoryAttachments.length > 0 && (
                    <SimpleTable
                        title={t("change.mandatoryAttachments")}
                        getRowId={(row) => row.type}
                        rows={mandatoryAttachments}
                        columns={columns(t("change.mandatoryAttachment"))}
                        rowDetail
                        getDetailPanelHeight={getDetailPanelHeight}
                        detailPanelExpandedRowIds={openedAttachmentsRows}
                        toggleRowCallback={toggleAttachmentRow}
                    />
                )}

                {optionalAttachments.length > 0 && (
                    <SimpleTable
                        title={t("change.optionalAttachments")}
                        getRowId={(row) => row.type}
                        rows={optionalAttachments}
                        columns={columns(t("change.optionalAttachment"))}
                        rowDetail
                        getDetailPanelHeight={getDetailPanelHeight}
                        detailPanelExpandedRowIds={openedAttachmentsRows}
                        toggleRowCallback={toggleAttachmentRow}
                    />
                )}
            </MultiDataTableWrapper>

            {/* Modals */}
            <AttachmentUploadModal attachmentType={attachmentTypeToUpload} />
        </>
    );
};
